import { omit } from "lodash";
import moment from "moment";
import { v5 as uuidv5 } from "uuid";

import type * as amplitude from "./configuration";

import { type PageId } from "@/constants";

import { type AbstractUser } from "../../../user";
import { isInvalidLocation, prettifyContext } from "../context";
import { type Logger } from "../logger";
import { type LoggerOptions } from "../options";

import {
  type AmplitudeNativeEventType,
  type AmplitudeEventId,
  type AmplitudeEventContainer,
} from "./constants";
import {
  type AmplitudeEventContext,
  type ParsedAmplitudeEventContext,
} from "./context";
import {
  AMPLITUDE_EVENTS,
  getAmplitudeEvent,
  type AmplitudeEvent,
  type AmplitudeEventDetails,
} from "./events";

const NORTHBEAM_EVENT_ID_NAMESPACE = "1b671a64-40d5-491e-99b0-da01ff1f3341";

/**
 * Generates an internal Northbeam Event ID that can be used to determine whether or not an observed
 * event in Amplitude is the same as another observed event.
 *
 * Background
 * ----------
 * When events are emitted to Amplitude, Amplitude will automatically assign the event an ID.  This
 * ID is determined based on the event message string - but not the event context.  So, two events
 * that have the same message string will have the same ID.
 *
 * To show why this is not desirable, consider the following example: The {@link AmplitudeEvent}
 * 'exportSalesChartData' is emitted from two different locations on the {@link Page} 'Sales' (i.e.
 * the {@link AmplitudeContainer}(s) are different).  In this case, those two events need to be
 * treated differently, because they were triggered from different user behavior.
 *
 * For this reason, at Northbeam, we need to consider two events as the same (which means they have
 * the same internal Northbeam Event ID 'eventId') based on whether or not they have the same values
 * for the following fields:
 *
 * 1. Page: The {@link Page} that the event was emitted from.
 * 2. Container: The {@link AmplitudeContainer} that the event was emitted from on that
 *    {@link Page}.
 * 3. Event: The specific event name, {@link AmplitudeEvent} (which is the event message string).
 *
 * If two logged events have the same values for those 3 fields, they will be assigned the same
 * internal Northbeam Event ID ('eventId') - which is what this method returns.  This 'eventId'
 * will then be included in the 'data' (or context) that is supplied to Amplitude's 'logEvent'
 * method.
 *
 * IMPORTANT:
 * ---------
 * Changing this method will cause all previously emitted and stored events in Amplitude to have
 * inconsistent internal 'eventId'(s), and this function should not be changed unless coordinating
 * with individuals responsible for the Amplitude data pipeline, who can ensure that a proper data
 * migration is performed.
 */
export const generateNorthbeamEventId = ({
  page,
  container,
  eventId,
}: {
  page: PageId;
  container: AmplitudeEventContainer;
  eventId: AmplitudeEventId;
}): string => {
  return uuidv5(
    `${page}-${container}-${eventId}`,
    NORTHBEAM_EVENT_ID_NAMESPACE,
  );
};

export type EventLoggerEventMethods<O extends LoggerOptions> = {
  [key in AmplitudeEventId as AmplitudeEvent<key>["camelCaseName"]]: (
    eventType: AmplitudeNativeEventType,
    context: AmplitudeEventContext<key, O>,
    options?: { emitToBrowser?: boolean },
  ) => void;
};

export type LogEvent<O extends LoggerOptions> = {
  <E extends AmplitudeEventId>(
    event: E,
    eventType: AmplitudeNativeEventType,
    context: AmplitudeEventContext<E, O>,
    options?: { emitToBrowser?: boolean },
  ): void;
} & EventLoggerEventMethods<O>;

/**
 * Parses the context that is provided to the {@link Logger}'s {@link Logger.event} method,
 * {@link AmplitudeEventContext}, into both the context that will be transmitted to Amplitude,
 * {@link ParsedAmplitudeEventContext}, and the string event name that will be transmitted to
 * Amplitude.
 */
export const parseAmplitudeEventArgs = <
  E extends AmplitudeEventId,
  U extends AbstractUser,
  O extends LoggerOptions,
  A extends amplitude.AmplitudeConfig,
>(
  logger: Logger<U, O, A>,
  eventId: E,
  type: AmplitudeNativeEventType,
  ctx: AmplitudeEventContext<E, O>,
): [ParsedAmplitudeEventContext<E, U>, string] | null => {
  const event = getAmplitudeEvent(eventId);

  const loc = (ctx as AmplitudeEventContext<E>).location || logger.location;
  if (loc === undefined) {
    /* Note: This should never happen because of typing, but we need to account for it beacuse the
       conditional types on the AmplitudeEventContext allow 'location' to be undefined if the
       options, O, are not inferred and we are inside the context of the inference. */
    throw new Error("Unexpectedly encountered undefined value for 'location'!");
  }

  /* Use the logger's transformation method to transform the user-related context parameters. */
  const base = logger.parseContext({
    user: logger.user,
    location: loc,
    previousLocation: ctx.previousLocation || logger.previousLocation,
  });
  /* The location will be invalid if there was an error parsing the location - most likely due to
     a path that is not associated with a page. */
  if (isInvalidLocation(base.location)) {
    logger.error(
      "The location in the event context could not be parsed, the event will not be logged " +
        `to Amplitude: ${base.location.error}`,
      { location: loc },
      { notify: true },
    );
    return null;
  }
  let container: AmplitudeEventContainer;
  if (ctx.container !== undefined) {
    container = ctx.container;
  } else if (logger.container !== undefined) {
    container = logger.container;
  } else {
    logger.error(
      "Unexpectedly encountered 'undefined' value for 'container' in the provided amplitude " +
        "event context and on the Logger instance!  This means that the 'container' was not " +
        "provided on instantiation of the Logger and was also not provided to the Logger's event " +
        "method.  This indicates a typing error, but for now... the event will not be logged to " +
        "Amplitude.",
      { location: loc },
      { notify: true },
    );
    return null;
  }
  let eventDetails: AmplitudeEventDetails<E> = {} as AmplitudeEventDetails<E>;
  /* Note: Right now, there are no AmplitudeEvent(s) that have null schemas - but we want to allow
     the schema to be null in the case that there is no additional context that is associated with
     the event. */
  const eventDetailsSchema = event.schema;
  if (eventDetailsSchema) {
    const parsed = eventDetailsSchema.safeParse(ctx.eventDetails);
    if (!parsed.success) {
      logger.error(
        `The provided event details for event '${event}' do not conform to the required schema!`,
        { location: loc, eventDetails: JSON.stringify(ctx.eventDetails) },
        { notify: true },
      );
      return null;
    }
    eventDetails = parsed.data as AmplitudeEventDetails<E>;
  }

  return [
    /* The event data that is provided to Amplitude, with null or undefined values removed from the
       context. */
    prettifyContext({
      ...(base as Pick<
        ParsedAmplitudeEventContext<E, U>,
        | "user"
        | "location"
        | "previousLocation"
        | "module"
        | "environment"
        | "nodeEnvironment"
      >),
      timestamp: ctx.timestamp
        ? moment(ctx.timestamp).format("YYYY-MM-DD HH:mm:ss")
        : moment().format("YYYY-MM-DD HH:mm:ss"),
      eventDetails: {
        ...eventDetails,
        prettyName: event.label,
        /* Internal Northbeam Event ID that can be used to determine whether or not an observed
           event in Amplitude is the same as another observed event. */
        internalId: generateNorthbeamEventId({
          page: base.location.page.id,
          container,
          eventId,
        }),
        type,
        path: {
          page: base.location.page,
          container,
        },
      },
    }),
    // The event message string that is provided to Amplitude.
    event.id,
  ];
};

/**
 * A factory function that creates the {@link Logger}'s {@link Logger.event} method.
 *
 * The factory function attributes sub-methods on the original event method to more conveniently
 * make calls to the {@link Logger}'s {@link Logger.event} method for specific events,
 * {@link AmplitudeEvent}:
 *
 * @example
 * logger.event("export-sales-chart-data", "click", { ... });
 *
 * @example
 * logger.event.exportSalesChartData("click", { ... });
 */
export const createEventLogger = <
  U extends AbstractUser,
  O extends LoggerOptions,
  A extends amplitude.AmplitudeConfig,
>(
  logger: Logger<U, O, A>,
): LogEvent<O> => {
  const eventLogger = <E extends AmplitudeEventId>(
    event: E,
    eventType: AmplitudeNativeEventType,
    context: AmplitudeEventContext<E, O>,
    options?: { emitToBrowser?: boolean },
  ) => {
    /* The 'emitToBrowser' option provided to the method itself should override any setting on the
       Logger instance if it is defined.  If it is not defined/provided, whether or not the event
       should be emitted to the browser should be determined by the setting on the Logger instance.
       This allows for the control of event browser submission to be both configured at the global
       level but also overridden at the local level for debugging purposes. */
    const emitToBrowser =
      options?.emitToBrowser === undefined
        ? logger.config.emitEventsToBrowser
        : options.emitToBrowser;

    if (logger.config.hasTransport("amplitude") || emitToBrowser) {
      /* The args will be null if any of the following conditions are met:
         1. The page associated with the location's pathname could not be resolved, either due to
            an invalid location or a Page that is not yet configured for that pathname.
         2. The event details do not conform to the schema that is associated with the event.
         3. The container is unexpectedly undefined - which should be prevented by TS.
         In any of those cases, the event will not be logged to Amplitude. */
      const args = parseAmplitudeEventArgs(logger, event, eventType, context);
      if (args) {
        if (logger.config.hasTransport("amplitude")) {
          logger.amplitudeInstance.logEvent(args[1], args[0]);
        }
        if (emitToBrowser) {
          logger.info(args[1], omit(args[0], "user"));
        }
      }
    }
  };

  return Object.assign(eventLogger, {
    ...AMPLITUDE_EVENTS.reduce(
      (
        prev: EventLoggerEventMethods<O>,
        eventData: AmplitudeEvent,
      ): EventLoggerEventMethods<O> => ({
        ...prev,
        [eventData.camelCaseName]: (
          eventType: AmplitudeNativeEventType,
          context: AmplitudeEventContext<(typeof eventData)["id"], O>,
          options?: { emitToBrowser?: boolean },
        ) => eventLogger(eventData.id, eventType, context, options),
      }),
      {} as EventLoggerEventMethods<O>,
    ),
  });
};
